import React from 'react';
import { WorkStatusBadge } from '../../../work/WorkStatusBadge';
import { PriorityChip } from '../../../work/PriorityChip';
import { DueDateLabel } from '../../../work/DueDateLabel';
import { ProgressBar } from '../../../work/ProgressBar';
import { STATUS_CONFIG, Priority } from '../../../../types/work';
import { RendererProps, getResultData } from './index';

export const WorkListRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  const items = Array.isArray(data) ? data : [];
  return (
    <div className="space-y-1.5">
      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {items.length} items
      </p>
      <div className="max-h-[60vh] overflow-y-auto space-y-1">
        {items.map((item: any, i: number) => (
          <div
            key={i}
            className={`flex items-center gap-2 p-2 rounded ${
              darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
            }`}
          >
            {item.status && <WorkStatusBadge status={item.status} />}
            <span
              className={`flex-1 text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}
            >
              {item.title || item.name || JSON.stringify(item).slice(0, 60)}
            </span>
            {item.priority && <PriorityChip priority={item.priority as Priority} />}
            {item.due_date && <DueDateLabel date={item.due_date} />}
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-gray-500 py-4 text-center">No results</p>
        )}
      </div>
    </div>
  );
};

export const WorkDetailRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  return (
    <div className="space-y-3">
      <div
        className={`p-4 rounded-lg border-l-4 border-l-emerald-500 ${
          darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
        }`}
      >
        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.title || 'Item'}
        </h3>
        {data.description && (
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {data.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {data.status && <WorkStatusBadge status={data.status} size="md" />}
          {data.priority && <PriorityChip priority={data.priority as Priority} />}
          {data.due_date && <DueDateLabel date={data.due_date} />}
        </div>
      </div>
      {data.task_breakdown && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className="text-xs text-gray-400 mb-2">Task Breakdown</p>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(data.task_breakdown).map(([status, count]) => (
              <div key={status} className="flex items-center gap-1.5">
                <span className="text-[10px]">{STATUS_CONFIG[status]?.icon || '·'}</span>
                <span className="text-xs text-gray-400">{status.replace(/_/g, ' ')}</span>
                <span
                  className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}
                >
                  {String(count)}
                </span>
              </div>
            ))}
          </div>
          {data.progress_pct != null && <ProgressBar pct={data.progress_pct} accent="emerald" />}
        </div>
      )}
      {data.entity_refs && data.entity_refs.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 mb-1">Entity Refs</p>
          <div className="flex flex-wrap gap-1">
            {data.entity_refs.map((ref: any, i: number) => (
              <span
                key={i}
                className={`px-2 py-0.5 rounded text-xs ${
                  darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {ref.entity_type}: {ref.label || ref.entity_id?.slice(0, 8)}
              </span>
            ))}
          </div>
        </div>
      )}
      {data.message && (
        <p className={`text-sm ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
          {data.message}
        </p>
      )}
    </div>
  );
};

export const WorkCreatedRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  return (
    <div
      className={`p-4 rounded-lg border ${
        darkMode ? 'bg-emerald-900/20 border-emerald-800/30' : 'bg-emerald-50 border-emerald-200'
      }`}
    >
      <p className={`text-sm font-medium ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
        {data.message || 'Created successfully'}
      </p>
      {data.projects_created != null && (
        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {data.projects_created} projects, {data.tasks_created} tasks
        </p>
      )}
      {data.title && (
        <p className={`text-sm mt-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{data.title}</p>
      )}
      {data.structure && (
        <div className="mt-2 space-y-1">
          {data.structure.map((s: any, i: number) => (
            <div key={i} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              📁 {s.project?.title} → {s.tasks?.length || 0} tasks
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const WorkDashboardRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  const renderedKeys = [
    'health_score',
    'counts',
    'goals',
    'projects',
    'tasks',
    'overdue_tasks',
    'blocked_tasks',
    'goals_without_projects',
    'projects_without_tasks',
    'unassigned_critical',
    'period_days',
    'scope',
  ];
  const extraEntries = Object.entries(data).filter(([k]) => !renderedKeys.includes(k));
  return (
    <div className="space-y-3">
      {data.health_score != null && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">Health Score</span>
            <span
              className={`text-lg font-bold ${
                data.health_score >= 70
                  ? 'text-emerald-400'
                  : data.health_score >= 40
                  ? 'text-amber-400'
                  : 'text-red-400'
              }`}
            >
              {data.health_score}
            </span>
          </div>
          <ProgressBar
            pct={data.health_score}
            accent={
              data.health_score >= 70 ? 'emerald' : data.health_score >= 40 ? 'amber' : 'red'
            }
          />
        </div>
      )}

      {data.counts && (
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(data.counts).map(([key, val]) => (
            <div
              key={key}
              className={`p-2 rounded-lg text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
            >
              <div
                className={`text-lg font-bold ${
                  (val as number) > 0
                    ? key === 'overdue' || key === 'blocked'
                      ? 'text-red-400'
                      : 'text-white'
                    : 'text-gray-600'
                }`}
              >
                {String(val)}
              </div>
              <div className="text-[10px] text-gray-500">{key.replace(/_/g, ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {data.goals && data.goals.total > 0 && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className="text-xs text-emerald-400 font-medium mb-2">🎯 Goals ({data.goals.total})</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(data.goals.by_status || {})
              .filter(([, c]) => (c as number) > 0)
              .map(([s, c]) => (
                <div key={s} className="flex items-center gap-1">
                  <WorkStatusBadge status={s} />
                  <span className="text-xs font-medium">{String(c)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {data.projects && data.projects.total > 0 && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className="text-xs text-teal-400 font-medium mb-2">
            📁 Projects ({data.projects.total})
          </p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(data.projects.by_status || {})
              .filter(([, c]) => (c as number) > 0)
              .map(([s, c]) => (
                <div key={s} className="flex items-center gap-1">
                  <WorkStatusBadge status={s} />
                  <span className="text-xs font-medium">{String(c)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {data.tasks && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className="text-xs text-violet-400 font-medium mb-2">✅ Tasks ({data.tasks.total})</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(data.tasks.by_status || {})
              .filter(([, c]) => (c as number) > 0)
              .map(([s, c]) => (
                <div key={s} className="flex items-center gap-1">
                  <WorkStatusBadge status={s} />
                  <span className="text-xs font-medium">{String(c)}</span>
                </div>
              ))}
          </div>
          {data.tasks.overdue > 0 && (
            <p className="text-xs text-red-400 mt-1">⚠ {data.tasks.overdue} overdue</p>
          )}
        </div>
      )}

      {data.overdue_tasks && data.overdue_tasks.length > 0 && (
        <div
          className={`p-3 rounded-lg border ${
            darkMode ? 'bg-red-900/10 border-red-800/30' : 'bg-red-50 border-red-200'
          }`}
        >
          <p className="text-xs text-red-400 font-medium mb-2">
            🔥 Overdue ({data.overdue_tasks.length})
          </p>
          {data.overdue_tasks.map((t: any, i: number) => (
            <div
              key={i}
              className={`flex items-center justify-between py-1 ${
                i > 0 ? 'border-t border-red-900/20' : ''
              }`}
            >
              <span className="text-sm text-red-300">{t.title}</span>
              <div className="flex items-center gap-2">
                {!t.assignee_id && (
                  <span className="text-[10px] bg-red-900/40 text-red-300 px-1.5 py-0.5 rounded">
                    UNASSIGNED
                  </span>
                )}
                <span className="text-xs text-red-400 font-mono">{t.days_overdue}d</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.blocked_tasks && data.blocked_tasks.length > 0 && (
        <div
          className={`p-3 rounded-lg border ${
            darkMode ? 'bg-amber-900/10 border-amber-800/30' : 'bg-amber-50 border-amber-200'
          }`}
        >
          <p className="text-xs text-amber-400 font-medium mb-2">
            ⛔ Blocked ({data.blocked_tasks.length})
          </p>
          {data.blocked_tasks.map((t: any, i: number) => (
            <div
              key={i}
              className={`flex items-center justify-between py-1 ${
                i > 0 ? 'border-t border-amber-900/20' : ''
              }`}
            >
              <span className="text-sm text-amber-300">{t.title}</span>
              {!t.assignee_id && (
                <span className="text-[10px] bg-amber-900/40 text-amber-300 px-1.5 py-0.5 rounded">
                  UNASSIGNED
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {data.goals_without_projects && data.goals_without_projects.length > 0 && (
        <div
          className={`p-3 rounded-lg border ${
            darkMode ? 'bg-sky-900/10 border-sky-800/30' : 'bg-sky-50 border-sky-200'
          }`}
        >
          <p className="text-xs text-sky-400 font-medium mb-2">
            🎯 Goals Without Projects ({data.goals_without_projects.length})
          </p>
          {data.goals_without_projects.map((g: any, i: number) => (
            <div key={i} className="text-sm text-sky-300 py-0.5">
              {g.title}
            </div>
          ))}
        </div>
      )}

      {data.projects_without_tasks && data.projects_without_tasks.length > 0 && (
        <div
          className={`p-3 rounded-lg border ${
            darkMode ? 'bg-teal-900/10 border-teal-800/30' : 'bg-teal-50 border-teal-200'
          }`}
        >
          <p className="text-xs text-teal-400 font-medium mb-2">
            📁 Projects Without Tasks ({data.projects_without_tasks.length})
          </p>
          {data.projects_without_tasks.map((p: any, i: number) => (
            <div key={i} className="text-sm text-teal-300 py-0.5">
              {p.title}
            </div>
          ))}
        </div>
      )}

      {data.unassigned_critical && data.unassigned_critical.length > 0 && (
        <div
          className={`p-3 rounded-lg border ${
            darkMode ? 'bg-purple-900/10 border-purple-800/30' : 'bg-purple-50 border-purple-200'
          }`}
        >
          <p className="text-xs text-purple-400 font-medium mb-2">
            ⚡ Unassigned Critical ({data.unassigned_critical.length})
          </p>
          {data.unassigned_critical.map((t: any, i: number) => (
            <div key={i} className="text-sm text-purple-300 py-0.5">
              {t.title}
            </div>
          ))}
        </div>
      )}

      {extraEntries.length > 0 && (
        <details className={`p-2 rounded text-xs ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <summary className="text-gray-500 cursor-pointer">Raw data</summary>
          <pre className="mt-1 whitespace-pre-wrap text-gray-400">
            {JSON.stringify(Object.fromEntries(extraEntries), null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

// Static Tailwind class maps for WorkStandupRenderer — dynamic interpolation
// like `text-${color}-400` breaks purge.
const STANDUP_SECTION_CLASSES: Record<
  string,
  {
    text: string;
    textLight: string;
    textDark: string;
    bgDark: string;
    bgLight: string;
    borderDark: string;
    borderLight: string;
    borderSep: string;
    headerText: string;
  }
> = {
  emerald: {
    text: 'text-emerald-400',
    textLight: 'text-emerald-700',
    textDark: 'text-emerald-300',
    bgDark: 'bg-emerald-900/10',
    bgLight: 'bg-emerald-50',
    borderDark: 'border-emerald-800/30',
    borderLight: 'border-emerald-200',
    borderSep: 'border-emerald-900/20',
    headerText: 'text-emerald-400',
  },
  teal: {
    text: 'text-teal-400',
    textLight: 'text-teal-700',
    textDark: 'text-teal-300',
    bgDark: 'bg-teal-900/10',
    bgLight: 'bg-teal-50',
    borderDark: 'border-teal-800/30',
    borderLight: 'border-teal-200',
    borderSep: 'border-teal-900/20',
    headerText: 'text-teal-400',
  },
  red: {
    text: 'text-red-400',
    textLight: 'text-red-700',
    textDark: 'text-red-300',
    bgDark: 'bg-red-900/10',
    bgLight: 'bg-red-50',
    borderDark: 'border-red-800/30',
    borderLight: 'border-red-200',
    borderSep: 'border-red-900/20',
    headerText: 'text-red-400',
  },
  sky: {
    text: 'text-sky-400',
    textLight: 'text-sky-700',
    textDark: 'text-sky-300',
    bgDark: 'bg-sky-900/10',
    bgLight: 'bg-sky-50',
    borderDark: 'border-sky-800/30',
    borderLight: 'border-sky-200',
    borderSep: 'border-sky-900/20',
    headerText: 'text-sky-400',
  },
  gray: {
    text: 'text-gray-400',
    textLight: 'text-gray-700',
    textDark: 'text-gray-300',
    bgDark: 'bg-gray-900/10',
    bgLight: 'bg-gray-50',
    borderDark: 'border-gray-800/30',
    borderLight: 'border-gray-200',
    borderSep: 'border-gray-900/20',
    headerText: 'text-gray-400',
  },
};

export const WorkStandupRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  const sectionConfig: Record<string, { color: string; icon: string; label: string }> = {
    completed: { color: 'emerald', icon: '✓', label: 'Completed' },
    in_progress: { color: 'teal', icon: '◑', label: 'In Progress' },
    blocked: { color: 'red', icon: '⊗', label: 'Blocked' },
    upcoming: { color: 'sky', icon: '📅', label: 'Upcoming' },
  };

  return (
    <div className="space-y-3">
      {data.velocity && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className="text-xs text-gray-400 mb-2">Velocity ({data.period_days || 7}d)</p>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{data.velocity.this_period}</p>
              <p className="text-[10px] text-gray-500">this period</p>
            </div>
            <div
              className={`text-2xl ${
                data.velocity.trend === 'up'
                  ? 'text-emerald-400'
                  : data.velocity.trend === 'down'
                  ? 'text-red-400'
                  : 'text-gray-500'
              }`}
            >
              {data.velocity.trend === 'up' ? '↑' : data.velocity.trend === 'down' ? '↓' : '→'}
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-500">{data.velocity.last_period}</p>
              <p className="text-[10px] text-gray-500">last period</p>
            </div>
          </div>
        </div>
      )}

      {data.summary_counts && (
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(data.summary_counts).map(([key, val]) => {
            const cfg = sectionConfig[key];
            return (
              <div
                key={key}
                className={`p-2 rounded-lg text-center ${
                  darkMode ? 'bg-gray-800' : 'bg-gray-50'
                }`}
              >
                <div
                  className={`text-lg font-bold ${
                    STANDUP_SECTION_CLASSES[cfg?.color || 'gray']?.text || 'text-gray-400'
                  }`}
                >
                  {String(val)}
                </div>
                <div className="text-[10px] text-gray-500">{cfg?.label || key}</div>
              </div>
            );
          })}
        </div>
      )}

      {['completed', 'in_progress', 'blocked', 'upcoming'].map((section) => {
        const items = data[section];
        if (!items || items.length === 0) return null;
        const cfg = sectionConfig[section];
        const cls = STANDUP_SECTION_CLASSES[cfg.color] || STANDUP_SECTION_CLASSES.gray;
        return (
          <div
            key={section}
            className={`p-3 rounded-lg border ${
              darkMode ? `${cls.bgDark} ${cls.borderDark}` : `${cls.bgLight} ${cls.borderLight}`
            }`}
          >
            <p className={`text-xs font-medium mb-2 ${cls.headerText}`}>
              {cfg.icon} {cfg.label} ({items.length})
            </p>
            {items.slice(0, 8).map((t: any, i: number) => (
              <div
                key={i}
                className={`flex items-center justify-between py-1 ${
                  i > 0 ? `border-t ${cls.borderSep}` : ''
                }`}
              >
                <span className={`text-sm ${darkMode ? cls.textDark : cls.textLight}`}>
                  {t.title}
                </span>
                {t.completed_at && (
                  <span className="text-[10px] text-gray-500">
                    {new Date(t.completed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            ))}
            {items.length > 8 && (
              <p className="text-[10px] text-gray-500 mt-1">+{items.length - 8} more</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const WorkDiffRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  return (
    <div
      className={`p-4 rounded-lg border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {data.message && (
        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.message}
        </p>
      )}
      {data.reassigned_count != null && (
        <p className="text-sm text-gray-400">
          {data.dry_run ? '(dry run) ' : ''}
          {data.reassigned_count} tasks {data.dry_run ? 'would be' : ''} reassigned
        </p>
      )}
      {data.updated_count != null && (
        <p className="text-sm text-gray-400">
          {data.dry_run ? '(dry run) ' : ''}
          {data.updated_count} items → {data.new_status}
          {data.cascade_count > 0 && ` + ${data.cascade_count} cascaded`}
        </p>
      )}
      {data.updated_items && data.updated_items.length > 0 && (
        <div className="mt-2 space-y-1">
          {data.updated_items.slice(0, 10).map((item: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">{item.title}</span>
              <span className="text-red-400">{item.old_status}</span>
              <span className="text-gray-600">→</span>
              <span className="text-emerald-400">{item.new_status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const WorkBurndownRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div
          className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
        >
          <div className="text-2xl font-bold text-emerald-400">{data.completed_total}</div>
          <div className="text-[10px] text-gray-500">completed</div>
        </div>
        <div
          className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
        >
          <div className="text-2xl font-bold text-violet-400">{data.remaining}</div>
          <div className="text-[10px] text-gray-500">remaining</div>
        </div>
        <div
          className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
        >
          <div className="text-2xl font-bold text-teal-400">{data.avg_daily_velocity}/d</div>
          <div className="text-[10px] text-gray-500">velocity</div>
        </div>
      </div>

      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>{data.total_tasks} total tasks</span>
          <span>{data.completion_pct}%</span>
        </div>
        <ProgressBar pct={data.completion_pct} accent="emerald" showLabel={false} />
      </div>

      {data.daily_completed && data.daily_completed.length > 0 && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className="text-xs text-gray-400 mb-2">Daily Completions ({data.period_days}d)</p>
          <div className="flex items-end gap-1 h-16">
            {data.daily_completed.map((d: any, i: number) => {
              const max = Math.max(
                ...data.daily_completed.map((x: any) => x.completed),
                1,
              );
              const height = Math.max(4, (d.completed / max) * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div
                    className="w-full bg-emerald-500 rounded-t"
                    style={{ height: `${height}%` }}
                    title={`${d.date}: ${d.completed}`}
                  />
                  {i % 3 === 0 && (
                    <span className="text-[8px] text-gray-600">{d.date.slice(5)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.daily_created && data.daily_created.length > 0 && (
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className="text-xs text-gray-400 mb-1">
            New tasks added:{' '}
            {data.daily_created.reduce((s: number, d: any) => s + d.created, 0)} in{' '}
            {data.period_days}d
          </p>
        </div>
      )}
    </div>
  );
};

export const WorkLeaderboardRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  return (
    <div className="space-y-3">
      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <p className="text-xs text-gray-400 mb-1">
          {data.total_contributors} contributors · {data.period_days}d
        </p>
      </div>
      {data.leaderboard &&
        data.leaderboard.map((entry: any, i: number) => (
          <div
            key={i}
            className={`flex items-center gap-3 p-3 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-gray-50'
            } ${i === 0 ? 'ring-1 ring-amber-500/30' : ''}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                i === 0
                  ? 'bg-amber-500 text-black'
                  : i === 1
                  ? 'bg-gray-400 text-black'
                  : i === 2
                  ? 'bg-amber-700 text-white'
                  : 'bg-gray-700 text-gray-400'
              }`}
            >
              {i + 1}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {entry.name}
              </p>
              <p className="text-[10px] text-gray-500">{entry.total_assigned} assigned</p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="text-center">
                <div className="text-emerald-400 font-bold">{entry.completed}</div>
                <div className="text-[9px] text-gray-600">done</div>
              </div>
              <div className="text-center">
                <div className="text-violet-400 font-bold">{entry.in_progress}</div>
                <div className="text-[9px] text-gray-600">active</div>
              </div>
              {entry.blocked > 0 && (
                <div className="text-center">
                  <div className="text-red-400 font-bold">{entry.blocked}</div>
                  <div className="text-[9px] text-gray-600">stuck</div>
                </div>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};

export const WorkForecastRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const data = getResultData(toolCall);
  return (
    <div className="space-y-3">
      <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.title}
        </p>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
          <span>
            {data.completed}/{data.total_tasks} tasks done
          </span>
          <span>{data.completion_pct}%</span>
          <span>{data.velocity_14d}/day velocity</span>
        </div>
        <ProgressBar pct={data.completion_pct} accent="teal" />
      </div>

      {data.forecast && data.forecast.status === 'complete' && (
        <div
          className={`p-4 rounded-lg border text-center ${
            darkMode
              ? 'bg-emerald-900/20 border-emerald-800/30'
              : 'bg-emerald-50 border-emerald-200'
          }`}
        >
          <span className="text-3xl">🎉</span>
          <p className="text-sm text-emerald-400 mt-2">{data.forecast.message}</p>
        </div>
      )}

      {data.forecast && data.forecast.status === 'stalled' && (
        <div
          className={`p-4 rounded-lg border ${
            darkMode ? 'bg-red-900/20 border-red-800/30' : 'bg-red-50 border-red-200'
          }`}
        >
          <p className="text-sm text-red-400">⚠️ {data.forecast.message}</p>
        </div>
      )}

      {data.forecast && data.forecast.expected && (
        <div className="space-y-2">
          {[
            { key: 'optimistic', label: '🟢 Optimistic', color: 'emerald' },
            { key: 'expected', label: '🟡 Expected', color: 'amber' },
            { key: 'pessimistic', label: '🔴 Pessimistic', color: 'red' },
          ].map(({ key, label, color }) => {
            const est = data.forecast[key];
            if (!est) return null;
            return (
              <div
                key={key}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  darkMode ? 'bg-gray-800' : 'bg-gray-50'
                }`}
              >
                <span className={`text-sm text-${color}-400`}>{label}</span>
                <div className="text-right">
                  <span className={`text-sm font-bold text-${color}-400`}>{est.date}</span>
                  <span className="text-xs text-gray-500 ml-2">({est.days}d)</span>
                </div>
              </div>
            );
          })}

          {data.forecast.due_date && (
            <div
              className={`p-3 rounded-lg border ${
                data.forecast.on_track
                  ? darkMode
                    ? 'bg-emerald-900/10 border-emerald-800/30'
                    : 'bg-emerald-50 border-emerald-200'
                  : darkMode
                  ? 'bg-red-900/10 border-red-800/30'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Due date</span>
                <span
                  className={`text-sm font-medium ${
                    data.forecast.on_track ? 'text-emerald-400' : 'text-red-400'
                  }`}
                >
                  {data.forecast.due_date.slice(0, 10)}
                </span>
              </div>
              <p
                className={`text-xs mt-1 ${
                  data.forecast.on_track ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                {data.forecast.on_track
                  ? '✓ On track to meet deadline'
                  : '⚠ At risk of missing deadline'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
